import clsx from 'clsx';
import React, { createContext } from 'react'
import './Table.scss'
export interface Header {
    name: string;
    component?: JSX.Element;
}


/* ------------------------------------------------------------------------- */
/* ---------------------------------- TABLE -------------------------------- */
/* ------------------------------------------------------------------------- */

interface Props {
    children: React.ReactNode;
    headers: Record<string, Header>;
    className?: string;
}
/**
 * @Component Table
 * @Description Renders a table with headers and child rows.
 * @Props
 *  - headers: Defines table column headers, usually corresponding to `TableRow` column keys.
 *  - children: Table rows.
 *  - className: To make unique table
 */


/**
 * @Headers headers
 * @Description Renders a table row with dynamic column data and custom components.
 * @Props 
 * ├── key1  → { name, jsx? } → element
 * ├── key2  → { table header column, Optional jsx } → element
 * ├── etc   → etc
 */
export function Table({ children, headers, className }: Props): JSX.Element {
    const tableHeader = Object.entries(headers as Record<string, Header>).map(([key, { name, component }]) => ({ key, name, component }));


    return (

        <div className={clsx("transaction-table", "transaction-table--" + className)}>
            <div className={clsx('transaction-table__header-container')}>
                {tableHeader.map(({ key, name, component }) => (
                    <span key={key} className={clsx('transaction-table__header-label', "transaction-table__header-label--" + key)}>
                        {name} {component && component}
                    </span>
                ))}
            </div>
            <div className={clsx('transaction-table__body-container')}>
                {children}
            </div>
        </div>
    )
}


/* ------------------------------------------------------------------------- */
/* ------------------------------- TABLE ROW ------------------------------- */
/* ------------------------------------------------------------------------- */

export type TableRowProps<T> = {
    columnData: T
    columnComponents: Record<keyof T, (key: string, value: any) => JSX.Element>;
    className: string;
};

/**
 * @Component TableRow
 * @Description Renders a table row with dynamic column data and custom components.
 * @Props 
 *  - columnData: Data For columnComponents
 *  - columnComponents: Custom Components mounted to row cells. 
 *  - className: To make unique table row ( keys from mapped data )
 * 
 * @NOTE - Example can be found in WalletTransactions.tsx
 */

/**
 * @ColumnData ColumnData ( DTO )
 * @Description Transformed PaymentTransaction data ( to be displayed in cell component )
 * 
 * @DataStructure
 * 
 * ColumnData (Transforms raw transaction data into structured keys to provide DTO to components)
 * ├── key1  → { value } → component1(key,value) 
 * ├── key2  → { value, action } → component2(key,action,value) 
 * ├── etc   → etc
 * 
 */

/**
 * @ColumnComponents ColumnComponents
 * @Description Maps each key in `columnData` to a corresponding rendering component. To add data ( props ) for component
 * 
 * @DataStructure
 * 
 * columnComponents (Mapping of Keys to Components)
 * ├── key1  → component1 (Receives: key, value)
 * ├── key2  → component2 (Receives: key, value, onClick)
 * ├── etc   → etc
 * 
 */

export function TableRow<T>({ columnData, columnComponents, className }: TableRowProps<T>): JSX.Element {
    return (
        <div className={clsx("transaction-table__row", "transaction-table__row--" + className)}>
            {Object.keys(columnData as keyof T).map((key) => {
                const value = columnData[key as keyof T];
                const renderColumn = columnComponents[key as keyof T];

                return (
                    <div key={key} className={clsx("transaction-table__cell", "transaction-table__cell--" + key)}>
                        {renderColumn(key, value)}
                    </div>
                );
            })}
        </div>
    );
}












